import React from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

type ResolverEntry = {
  employeeId: string
  employeeName: string
  resolvedCount: number
  averageResolutionTimeSeconds: number
  slaCompliancePercent: number
}

type Props = {
  data: ResolverEntry[]
}

const formatDuration = (seconds: number): string => {
  if (seconds < 3600) {
    const mins = Math.round(seconds / 60)
    return `${mins}m`
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

const COLUMN_WIDTHS = { name: '40%', resolved: '20%', avgTime: '20%', sla: '20%' } as const

const HeaderCell: React.FC<{ label: string; width: string; align?: 'left' | 'right' }> = ({
  label,
  width,
  align = 'left',
}) => (
  <F0Box
    width={width}
    paddingX="sm"
    paddingY="xs"
    display="flex"
    justifyContent={align === 'right' ? 'end' : 'start'}
  >
    <F0Text variant="label" content={label} />
  </F0Box>
)

const DataCell: React.FC<{ value: string; width: string; align?: 'left' | 'right' }> = ({
  value,
  width,
  align = 'left',
}) => (
  <F0Box
    width={width}
    paddingX="sm"
    paddingY="xs"
    display="flex"
    justifyContent={align === 'right' ? 'end' : 'start'}
  >
    <F0Text variant="body" content={value} />
  </F0Box>
)

export const InsightsResolverTable: React.FC<Props> = ({ data }) => {
  if (data.length === 0) {
    return (
      <Widget header={{ title: i18n.t('ticketing.inbox.insights.resolver_table.title') }}>
        <F0Box paddingX="md" paddingY="md">
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t('ticketing.inbox.insights.resolver_table.empty_description')}
          />
        </F0Box>
      </Widget>
    )
  }

  return (
    <Widget header={{ title: i18n.t('ticketing.inbox.insights.resolver_table.title') }}>
      <F0Box display="flex" flexDirection="column">
        {/* Header row */}
        <F0Box display="flex" flexDirection="row">
          <HeaderCell
            label={i18n.t('ticketing.inbox.insights.resolver_table.employee')}
            width={COLUMN_WIDTHS.name}
          />
          <HeaderCell
            label={i18n.t('ticketing.inbox.insights.resolver_table.tickets_resolved')}
            width={COLUMN_WIDTHS.resolved}
            align="right"
          />
          <HeaderCell
            label={i18n.t('ticketing.inbox.insights.resolver_table.avg_time')}
            width={COLUMN_WIDTHS.avgTime}
            align="right"
          />
          <HeaderCell
            label={i18n.t('ticketing.inbox.insights.resolver_table.sla_compliance')}
            width={COLUMN_WIDTHS.sla}
            align="right"
          />
        </F0Box>
        {/* Data rows */}
        {data.map((resolver) => (
          <F0Box key={resolver.employeeId} display="flex" flexDirection="row">
            <DataCell value={resolver.employeeName} width={COLUMN_WIDTHS.name} />
            <DataCell
              value={String(resolver.resolvedCount)}
              width={COLUMN_WIDTHS.resolved}
              align="right"
            />
            <DataCell
              value={formatDuration(resolver.averageResolutionTimeSeconds)}
              width={COLUMN_WIDTHS.avgTime}
              align="right"
            />
            <DataCell
              value={`${resolver.slaCompliancePercent}%`}
              width={COLUMN_WIDTHS.sla}
              align="right"
            />
          </F0Box>
        ))}
      </F0Box>
    </Widget>
  )
}
